import React, { useState, useEffect, useRef } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, LogOut as LogOutIcon, Image, Printer } from 'lucide-react';

const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);

export default function FrontDeskLogViewer({ date, onClose }) {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [expandedSig, setExpandedSig] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (date) params.set('date', date);
        if (filter !== 'all') params.set('logType', filter);
        const res = await safeFetch(`${API}/summer2026/frontdesk/logs?${params}`);
        const data = await res.json();
        if (data.success) setLogs(data.logs);
      } catch (e) {
        console.error('Error fetching logs:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [date, filter]);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    const dateDisplay = date
      ? new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      : 'All Dates';

    printWindow.document.write(`
      <html>
      <head>
        <title>Late Arrivals & Early Dismissals - ${dateDisplay}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          h1 { font-size: 18px; text-align: center; margin-bottom: 4px; }
          h2 { font-size: 14px; text-align: center; color: #666; margin-bottom: 20px; font-weight: normal; }
          .entry { border: 1px solid #ddd; border-radius: 8px; padding: 12px; margin-bottom: 12px; page-break-inside: avoid; }
          .entry-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
          .camper-name { font-size: 16px; font-weight: bold; }
          .bunk { font-size: 12px; color: #666; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: bold; }
          .badge-late { background: #fef3c7; color: #92400e; }
          .badge-early { background: #ccfbf1; color: #115e59; }
          .details { font-size: 13px; color: #555; margin: 4px 0; }
          .details strong { color: #333; }
          .notes { font-size: 12px; color: #666; font-style: italic; }
          .sig-img { max-height: 60px; margin-top: 8px; border: 1px solid #eee; border-radius: 4px; }
          .summary { text-align: center; font-size: 13px; color: #666; margin-top: 20px; padding-top: 12px; border-top: 1px solid #ddd; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <h1>Rolling River Day Camp - Late Arrivals & Early Dismissals</h1>
        <h2>${dateDisplay}</h2>
        ${logs.length === 0 ? '<p style="text-align:center;color:#999;">No records for this date</p>' :
          logs.map(log => `
            <div class="entry">
              <div class="entry-header">
                <div>
                  <div class="camper-name">${log.camperName}</div>
                  <div class="bunk">${log.bunkCode} — ${log.bunkName}</div>
                </div>
                <span class="badge ${log.logType === 'late_arrival' ? 'badge-late' : 'badge-early'}">
                  ${log.logType === 'late_arrival' ? 'Late Arrival' : 'Early Dismissal'}
                </span>
              </div>
              <div class="details"><strong>Time:</strong> ${log.time} | <strong>Date:</strong> ${log.date}</div>
              <div class="details"><strong>Signed by:</strong> ${log.signerName}</div>
              ${log.additionalPeople ? `<div class="details"><strong>Additional People:</strong> ${log.additionalPeople}</div>` : ''}
              ${log.expectedTime ? `<div class="details"><strong>Expected Time:</strong> ${log.expectedTime}</div>` : ''}
              ${log.notes ? `<div class="notes">Notes: ${log.notes}</div>` : ''}
              ${log.signatureData && log.signatureData !== 'data:image/png;base64,test' ? `<img class="sig-img" src="${log.signatureData}" alt="Signature" />` : ''}
            </div>
          `).join('')
        }
        <div class="summary">
          Total: ${logs.length} record${logs.length !== 1 ? 's' : ''} |
          Late Arrivals: ${logs.filter(l => l.logType === 'late_arrival').length} |
          Early Dismissals: ${logs.filter(l => l.logType === 'early_dismissal').length}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const dateDisplay = date
    ? new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : 'All Dates';

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <div className="bg-[#0055A4] py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={onClose} className="text-white mr-3" data-testid="log-viewer-close">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-white text-xl font-bold font-['Fredoka']">Late Arrivals & Early Dismissals</h1>
          </div>
          <Button
            onClick={handlePrint}
            variant="ghost"
            className="text-white hover:bg-white/20 rounded-xl"
            data-testid="log-print-btn"
          >
            <Printer className="w-5 h-5 mr-1" /> Print
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4" ref={printRef}>
        <p className="text-sm text-[#475569] mb-3 font-medium">{dateDisplay}</p>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4">
          {[
            { id: 'all', label: 'All' },
            { id: 'late_arrival', label: 'Late Arrivals' },
            { id: 'early_dismissal', label: 'Early Dismissals' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                filter === f.id
                  ? 'bg-[#0055A4] text-white'
                  : 'bg-white border border-[#E2E8F0] text-[#475569] hover:bg-gray-50'
              }`}
              data-testid={`log-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#94A3B8]">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="bg-white rounded-2xl border p-8 text-center">
            <p className="text-[#94A3B8]">No records for this date</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {logs.map((log, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-4"
                  data-testid={`log-entry-${idx}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-[#0F172A] text-lg">{log.camperName}</p>
                      <p className="text-xs text-[#475569]">{log.bunkCode} — {log.bunkName}</p>
                    </div>
                    <Badge className={log.logType === 'late_arrival'
                      ? 'bg-amber-100 text-amber-800 border border-amber-200'
                      : 'bg-teal-100 text-teal-800 border border-teal-200'}>
                      {log.logType === 'late_arrival' ? (
                        <><Clock className="w-3 h-3 mr-1" />Late Arrival</>
                      ) : (
                        <><LogOutIcon className="w-3 h-3 mr-1" />Early Dismissal</>
                      )}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-[#475569] mb-2">
                    <span className="font-semibold">{log.time}</span>
                    <span>{log.date}</span>
                  </div>

                  <div className="text-sm mb-1">
                    <span className="text-[#475569]">Signed by: </span>
                    <span className="font-semibold text-[#0F172A]">{log.signerName}</span>
                  </div>

                  {log.additionalPeople && (
                    <p className="text-sm text-[#475569] mb-1">
                      <span className="font-medium">Additional People:</span> {log.additionalPeople}
                    </p>
                  )}

                  {log.expectedTime && (
                    <p className="text-sm text-[#475569] mb-1">
                      <span className="font-medium">Expected Time:</span> {log.expectedTime}
                    </p>
                  )}

                  {log.notes && (
                    <p className="text-xs text-[#475569] mb-2 italic">Notes: {log.notes}</p>
                  )}

                  <div className="mt-2">
                    <button
                      onClick={() => setExpandedSig(expandedSig === idx ? null : idx)}
                      className="text-xs text-[#0055A4] hover:underline flex items-center gap-1"
                    >
                      <Image className="w-3 h-3" />
                      {expandedSig === idx ? 'Hide' : 'View'} Signature
                    </button>
                    {expandedSig === idx && log.signatureData && (
                      <img
                        src={log.signatureData}
                        alt={`Signature by ${log.signerName}`}
                        className="mt-2 border rounded-lg max-h-32 bg-white"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-white rounded-2xl border p-4 mt-4 text-center text-sm text-[#475569]">
              <span className="font-semibold">{logs.length}</span> total |{' '}
              <span className="text-amber-700 font-medium">{logs.filter(l => l.logType === 'late_arrival').length} Late</span> |{' '}
              <span className="text-teal-700 font-medium">{logs.filter(l => l.logType === 'early_dismissal').length} Early</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
